import type { Identifier } from "react-admin";
import type {
  AuditFields,
  ClientRef,
  CommonFields,
  DataSource,
  DateYMDString,
  PartnerRef,
  Policy,
  ValidAny,
} from "@/types";

// ==================== ENUMS ====================

export enum LaseredAttributesType {
  DX_CODE = "dx_code",
  PLACE_OF_SERVICE = "place_of_service",
  PROCEDURE_CODE = "procedure_code",
}

export type ClaimantGender = "male" | "female";

export const CLAIMANT_GENDER_CHOICES: { id: ClaimantGender; name: string }[] = [
  { id: "male", name: "Male" },
  { id: "female", name: "Female" },
];

export type ClaimantRelationship = "self" | "dependent" | "spouse" | "other";

export const CLAIMANT_RELATIONSHIP_CHOICES: { id: ClaimantRelationship; name: string }[] = [
  { id: "self", name: "Subscriber" },
  { id: "spouse", name: "Spouse" },
  { id: "dependent", name: "Dependent" },
  { id: "other", name: "Other" },
];

/**
 * Stored type of a straight laser (`laser_type`) for a claimant on a policy.
 *
 * - `individual`: a straight laser with a fixed elevated deductible.
 * - `condition_based`: legacy. A straight laser recorded with a qualifying
 *   condition for reference; its deductible applies to all claims. No longer
 *   offered (PO 2026-10-07): the UI shows it as a Standard laser and sends its
 *   stored values back unchanged. NOT the operational `lasered_conditions[]` on
 *   {@link LaseredClaimantListItem} (armed/triggerable conditional lasers).
 *
 * The UI's laser types (Standard / Contract / Conditional) are `LaserKind` in
 * `@/utils/helpers/laserKind`.
 */
export type LaserType = "individual" | "condition_based";

export type MedicareQualifyingEvent = "age" | "disability" | "esrd";

export const MEDICARE_QUALIFYING_EVENT_CHOICES: { id: MedicareQualifyingEvent; name: string }[] = [
  { id: "age", name: "Age" },
  { id: "disability", name: "Disability" },
  { id: "esrd", name: "ESRD" },
];

export type DisabilityType = "std" | "ltd" | "ssdi" | "workers_comp";

export const DISABILITY_TYPE_CHOICES: { id: DisabilityType; name: string }[] = [
  { id: "std", name: "Short Term Disability" },
  { id: "ltd", name: "Long Term Disability" },
  { id: "ssdi", name: "SSDI" },
  { id: "workers_comp", name: "Workers Comp" },
];

export type COBRAQualifyingEvent =
  | "termination"
  | "hours_reduction"
  | "divorce"
  | "death"
  | "medicare"
  | "dependent_age_out"
  | "employer_bankrupt";

export const COBRA_QUALIFYING_EVENT_CHOICES: { id: COBRAQualifyingEvent; name: string }[] = [
  { id: "termination", name: "Termination" },
  { id: "hours_reduction", name: "Hours Reduction" },
  { id: "divorce", name: "Divorce" },
  { id: "death", name: "Death" },
  { id: "medicare", name: "Medicare" },
  { id: "dependent_age_out", name: "Dependent Age Out" },
  { id: "employer_bankrupt", name: "Employer Bankrupt" },
];

export type FMLAType = "continuous" | "intermittent" | "reduced";

export const FMLA_TYPE_CHOICES: { id: FMLAType; name: string }[] = [
  { id: "continuous", name: "Continuous" },
  { id: "intermittent", name: "Intermittent" },
  { id: "reduced", name: "Reduced" },
];

export type PremiumPayer = "employer" | "employee" | "shared";

export const PREMIUM_PAYER_CHOICES: { id: PremiumPayer; name: string }[] = [
  { id: "employer", name: "Employer" },
  { id: "employee", name: "Employee" },
  { id: "shared", name: "Shared" },
];

// ==================== SHARED STRUCTURES ====================

export interface DateRange {
  start_date?: DateYMDString;
  end_date?: DateYMDString;
}

export interface LaseredAttributes {
  laser_type?: LaserType;
  laser_reason?: string;
  amt_lasered_deductible?: number;
  amt_incremental_lasered_deductible?: number;
  conditions?: Record<string, ValidAny>;
  effective_date?: DateYMDString;
}

/**
 * Derived trigger state of a {@link LaseredCondition} (Feature #3502). The
 * server derives it on every read from the claimant's qualifying claim lines,
 * so it can move both ways (a deleted or reversed line clears a trigger):
 * - `armed`: no qualifying line yet.
 * - `triggered`: a qualifying medical line falls inside both windows.
 * - `inert`: the claimant is contract-lasered or contract-excluded on the policy;
 *   contract tiers sit above conditional lasers, so the condition has no effect.
 */
export type LaseredConditionTriggerState = "armed" | "triggered" | "inert";

/**
 * State of a {@link LaseredConditionExpectedTrigger} (#7361), server-derived:
 * - `active`: set, not cancelled, no qualifying line yet.
 * - `overdue`: active and its review-by date has passed (an `expected_trigger_overdue` review opens).
 * - `arrived`: a real qualifying line exists; the line's date of service replaced the expected one.
 * - `cancelled`: a user cancelled it.
 */
export type ExpectedTriggerState = "active" | "overdue" | "arrived" | "cancelled";

/**
 * An expected trigger on a conditional laser (#7361): the TPA reports a
 * qualifying claim that has not loaded yet, so the condition counts as
 * triggered on the expected date of service until the real line arrives.
 * Absent when never set.
 */
export interface LaseredConditionExpectedTrigger {
  expected_date: DateYMDString;
  reason: string;
  review_by: DateYMDString;
  set_by?: string;
  set_datetime?: string;
  state: ExpectedTriggerState;
  cancelled_by?: string;
  cancelled_datetime?: string;
  cancel_reason?: string;
}

/** Body for `PUT .../lasered-conditions/{conditionId}/expected-trigger` (replaces any earlier one). */
export interface SetExpectedTriggerRequest {
  expected_date: DateYMDString;
  reason: string;
  /** Optional: the API defaults it to expected_date + 60 days, capped at the paid window's end. */
  review_by?: DateYMDString;
}

/** Body for `POST .../lasered-conditions/{conditionId}/expected-trigger/cancel`. */
export interface CancelExpectedTriggerRequest {
  reason: string;
}

/**
 * A conditional laser on a claimant for one policy term. When a qualifying
 * claim line matches its ICD (prefix) or CPT (exact) codes inside both windows,
 * its deductible replaces the claimant's specific deductible from the line's
 * date of service; the most recent effective date wins (plan §2).
 *
 * Read from `/claimants/{claimantId}/lasered-conditions?policy_id={policyId}`
 * and embedded in {@link LaseredClaimantListItem.lasered_conditions}. Trigger
 * fields and `is_effective` are server-derived and ignored on write.
 */
export interface LaseredCondition {
  id: string;
  claimant_id_ref?: string;
  policy_id_ref?: string;
  lasered_condition_name: string;
  icd_codes: string[];
  cpt_codes: string[];
  amt_lasered_conditional_deductible: number;
  incurred_start_date: DateYMDString;
  incurred_end_date: DateYMDString;
  paid_start_date: DateYMDString;
  paid_end_date: DateYMDString;
  lasered_condition_trigger_state: LaseredConditionTriggerState;
  /**
   * Date of service of the qualifying line. Present when triggered; the
   * expected date when an active expected trigger is the source (#7361).
   */
  trigger_date?: DateYMDString;
  /** Absent when the trigger comes from an expected trigger. */
  triggered_by_claim_item_id?: string;
  /** Expected trigger (#7361); absent when never set. */
  expected_trigger?: LaseredConditionExpectedTrigger;
  /** True when this condition is the source of the claimant's effective deductible. */
  is_effective: boolean;
  /** Non-blocking advisory returned by a write, e.g. inert under a contract laser. */
  warning?: string;
  created_by?: string;
  created_datetime?: string;
  modified_datetime?: string;
}

/** Body for creating or replacing a {@link LaseredCondition}. */
export interface LaseredConditionRequest {
  lasered_condition_name: string;
  icd_codes: string[];
  cpt_codes: string[];
  amt_lasered_conditional_deductible: number;
  incurred_start_date: DateYMDString;
  incurred_end_date: DateYMDString;
  paid_start_date: DateYMDString;
  paid_end_date: DateYMDString;
}

/**
 * Where the server took a claimant's effective specific deductible from
 * (`claimant_policy_snapshots.effective_deductible_source`, plan §4.4).
 */
export type EffectiveDeductibleSource =
  | "specific"
  | "lasered"
  | "conditional_laser"
  | "modification"
  | "contract_laser"
  | "contract_excluded"
  | "review_decision";

/**
 * Contract-laser / full-exclusion config for a claimant on a policy (Feature
 * #5287). A contract laser is an elevated specific deductible scoped to a
 * contract period (incurred + paid date windows) with specific CPT procedures
 * excluded from the spec deductible. A full exclusion (`is_contract_excluded`)
 * carves the claimant out of the contract entirely — the two flags are mutually
 * exclusive. Sourced from `/lasered-claimants` list enrichment and the
 * `/contract-laser` write endpoint; stored as typed columns on the claimant
 * policy snapshot, NOT on {@link PolicyData}. At most one per claimant/policy.
 */
export interface ContractLaser {
  is_contract_lasered: boolean;
  amt_contract_lasered_deductible?: number;
  contract_incurred_start_date?: DateYMDString;
  contract_incurred_end_date?: DateYMDString;
  contract_paid_start_date?: DateYMDString;
  contract_paid_end_date?: DateYMDString;
  excluded_procedures?: string[];
  is_contract_excluded: boolean;
}

/**
 * Response from `GET/PUT /policies/{policyId}/lasered-claimants/{claimantId}/contract-laser`.
 * Same shape as {@link ContractLaser} plus an optional non-blocking advisory
 * (`warning`) the API surfaces after a write (e.g. a pre-existing straight laser).
 */
export interface ContractLaserResponse extends ContractLaser {
  warning?: string;
}

/** Request body for `PUT .../contract-laser`. */
export interface ContractLaserRequest {
  is_contract_lasered: boolean;
  is_contract_excluded: boolean;
  amt_contract_lasered_deductible?: number;
  contract_incurred_start_date?: DateYMDString;
  contract_incurred_end_date?: DateYMDString;
  contract_paid_start_date?: DateYMDString;
  contract_paid_end_date?: DateYMDString;
  excluded_procedures?: string[];
}

/** Response from `GET /policies/{policyId}/lasered-claimants/contract-lasers-summary`. */
export interface ContractLaserSummary {
  policy_id_ref: string;
  contract_lasered_count: number;
  excluded_count: number;
  total_contract_deductible: number;
  total_spend_over_deductible: number;
}

/**
 * API response item from `GET /policies/{policyId}/lasered-claimants`.
 * Flat structure with claimant info + lasered attributes for that policy.
 */
export interface LaseredClaimantListItem {
  id: string;
  claimant_name?: string;
  claimant_first_name?: string;
  claimant_last_name?: string;
  member_id?: string;
  /**
   * A straight laser is on file. False for a conditions-only row (only
   * conditional lasers, `amt_lasered_deductible` may read 0). Older APIs omit
   * it; see {@link hasStraightLaser}.
   */
  is_lasered?: boolean;
  laser_type?: LaserType;
  laser_reason?: string;
  amt_lasered_deductible: number;
  amt_incremental_lasered_deductible: number;
  /**
   * Untyped descriptive metadata for a single straight laser whose
   * {@link LaserType} is `condition_based` (e.g. `qualifying_condition`,
   * `icd_codes`, `cpt_codes` keyed by attribute). Distinct from
   * {@link lasered_conditions} below, which is the typed array of
   * armed/triggerable conditional lasers, each with its own deductible
   * and trigger state.
   */
  conditions?: Record<string, ValidAny>;
  lasered_conditions?: LaseredCondition[];
  effective_date?: DateYMDString;
  /** Server-resolved specific deductible (absent until the worker refreshes the snapshot). */
  effective_specific_deductible?: number;
  effective_deductible_source?: EffectiveDeductibleSource;
  effective_deductible_date?: DateYMDString;
  /**
   * A deductible review is open: the resolution is ambiguous and stop-loss
   * claims for this claimant and policy are held until a user resolves it.
   */
  is_deductible_review_pending?: boolean;
  /**
   * Contract-laser / full-exclusion config for this claimant on the policy.
   * At most one per claimant/policy; absent when neither is configured. See
   * {@link ContractLaser}.
   */
  contract_laser?: ContractLaser;
}

/**
 * Whether a lasered-claimants row has a straight laser: the server's
 * `is_lasered` when sent, else a laser type on the row (older APIs).
 */
export const hasStraightLaser = (row: Pick<LaseredClaimantListItem, "is_lasered" | "laser_type">): boolean =>
  row.is_lasered ?? !!row.laser_type;

// ==================== CLAIMS DATA ====================

export interface ClaimsValues {
  total_paid?: number;
  total_medical?: number;
  total_rx?: number;
  total_denied?: number;
  total_adjustments?: number;
  spend_over_deductible?: number;
  /**
   * Aggregate Specific Deductible allocation, per claimant (Feature #2934, PR 2773).
   * Written by the ASD engine on ASD-enabled policies; absent on non-ASD policies.
   * Engine invariant: `amt_specific_excess = amt_asd_consumed + amt_carrier_payable`.
   *
   * These explain a $0 Owed on an ASD policy: the claimant IS over their specific
   * deductible, but their excess has been absorbed by the aggregate corridor and is
   * not yet payable by the carrier (#3302).
   */
  /** Paid spend over the claimant's effective (standard or lasered) specific deductible. */
  amt_specific_excess?: number;
  /** Portion of that excess absorbed by the policy's aggregate specific deductible. */
  amt_asd_consumed?: number;
  /** Portion the carrier owes = excess − consumed. Zero while the corridor accumulates. */
  amt_carrier_payable?: number;
  /** Portion the carrier has actually paid. */
  amt_carrier_paid?: number;
  /** Negative adjustments applied to this claimant's ASD allocation. */
  amt_adjustment_total?: number;
  /**
   * What the deductible basis dropped from this term's stamped claim lines
   * (ADR-081 D5.5, #7338). Calculated bundle only; absent = not computed (the
   * snapshot has not been basis-recomputed since the columns shipped) and the
   * card shows no line; 0 = computed, nothing dropped ($0.00). Read through
   * `getExcludedByBasis`, which hides a type's figure where that type's
   * authority is reported.
   */
  /** Medical dollars dropped by the paid/incurred window or a missing paid/service date. */
  excluded_window_medical?: number;
  /** Rx counterpart of excluded_window_medical. */
  excluded_window_rx?: number;
  /** Medical dollars on lines whose claim_status is not paid (submitted, processing, approved, denied). */
  excluded_not_paid_medical?: number;
  /** Rx counterpart of excluded_not_paid_medical. */
  excluded_not_paid_rx?: number;
  /**
   * The date this value is current through (ISO datetime). For a carrier-
   * reported YTD this is the reviewer-confirmed as-of (paid-through) date that
   * drives claims authority (ADR-051); for calculated values it's the latest
   * claim paid date. Surfaced on the claims source chip as "As of …".
   */
  paid_through_date?: string;
}

/**
 * One claim type's resolved authority under a per-type (Rx carve-out) policy
 * (ADR-077). Mirrors the Go PerTypeAuthorityInfo. `total_paid` is that type's
 * authoritative amount (reported or calculated, per its own paid-through date);
 * `as_of` is that type's paid-through date.
 */
export interface PerTypeAuthorityInfo {
  authority: DataSource;
  total_paid: number;
  source_name?: string;
  as_of?: string;
}

/**
 * Per-claim-type authority breakdown for a carve-out policy: medical and Rx
 * each resolve reported-vs-calculated independently, and the authoritative
 * claimant total is medical.total_paid + rx.total_paid (ADR-077 §1/§5).
 * Present ONLY on carve-out claimants; bundled claimants use the scalar
 * `ClaimsData.authority`.
 */
export interface AuthorityByType {
  medical: PerTypeAuthorityInfo;
  rx: PerTypeAuthorityInfo;
}

export interface ClaimsData {
  calculated?: ClaimsValues;
  reported?: ClaimsValues;
  authority?: DataSource; // ← renamed to match Go
  source_document_id?: Identifier;
  source_name?: string;
  last_updated?: string;
}

// ==================== STOPLOSS DATA ====================

export interface StoplossValues {
  total_submitted?: number;
  total_reimbursed?: number;
  prior_submissions?: number;
  claim_count?: number;
  claim_count_submitted?: number;
  claim_count_reimbursed?: number;
  claim_count_denied?: number;
}

export interface StoplossData {
  calculated?: StoplossValues;
  last_updated?: string; // ISO datetime
}

// ==================== REIMBURSEMENTS DATA ====================

export interface ReimbursementValues {
  total_requested?: number;
  total_reimbursed?: number;
  total_adjustments?: number;
  total_denied?: number;
  total_pended?: number;
  record_count?: number;
}

export interface ReimbursementReported {
  from_eor?: ReimbursementValues;
  from_ier?: ReimbursementValues;
  from_internal?: ReimbursementValues;
}

export interface ReimbursementsData {
  reported?: ReimbursementReported;
  source?: DataSource;
  last_updated?: string; // ISO datetime
}

// ==================== MEMBER STATUS ====================

export interface MedicareStatus {
  qualifying_event?: MedicareQualifyingEvent;
  part_a_effective?: DateYMDString;
  part_b_effective?: DateYMDString;
  part_c_effective?: DateYMDString;
  part_d_effective?: DateYMDString;
}

export interface DisabilityStatus {
  disability_type?: DisabilityType;
  disability_dates?: DateRange[];
  ssdi_effective_date?: DateYMDString;
}

export interface COBRAStatus {
  qualifying_event?: COBRAQualifyingEvent;
  qualifying_date?: DateYMDString;
  election_date?: DateYMDString;
  coverage_dates?: DateRange[];
  max_coverage_months?: number;
  monthly_premium?: number;
  premiums_paid?: number;
}

export interface FMLAStatus {
  fmla_type?: FMLAType;
  leave_dates?: DateRange[];
  total_hours_used?: number;
  total_hours_available?: number;
  hours_scheduled_work?: number;
  reason?: string;
  is_paid_leave?: boolean;
  is_concurrent_with_loa?: boolean;
  premium_paid_by?: PremiumPayer;
  premium_paid_through?: DateYMDString;
}

export interface MedicalLOAStatus {
  leave_dates?: DateRange[];
  premium_paid_by?: PremiumPayer;
  premium_paid_through?: DateYMDString;
}

export interface StatusData {
  // Work Status
  is_actively_working?: boolean;
  last_active_work_date?: DateYMDString;
  return_to_work_date?: DateYMDString;

  // Retirement
  is_retired?: boolean;
  retirement_date?: DateYMDString;

  // Medicare
  is_medicare_eligible?: boolean;
  medicare?: MedicareStatus;

  // Disability
  is_on_disability?: boolean;
  disability?: DisabilityStatus;

  // Sick Leave
  is_sick_leave_used?: boolean;
  sick_leave_dates?: DateRange[];

  // Vacation Leave
  is_vacation_leave_used?: boolean;
  vacation_dates?: DateRange[];

  // FMLA
  is_fmla_used?: boolean;
  fmla?: FMLAStatus;

  // Medical LOA
  is_medical_loa_used?: boolean;
  medical_loa?: MedicalLOAStatus;

  // Termination
  is_terminated?: boolean;
  employment_termination_date?: DateYMDString;
  benefits_termination_date?: DateYMDString;

  // COBRA
  is_cobra_applicable?: boolean;
  cobra?: COBRAStatus;
}

export interface EnrollmentData {
  is_dependent?: boolean;
  claimant_info?: ClaimantEnrollmentInfo;
  employee_info?: EmployeeEnrollmentInfo;
}

export interface ClaimantEnrollmentInfo {
  original_effective_date?: DateYMDString;
  current_effective_date?: DateYMDString;
  plan_name?: string;
  date_of_birth?: DateYMDString;
  zip_code?: string;
}

export interface EmployeeEnrollmentInfo {
  hire_date?: DateYMDString;
  original_effective_date?: DateYMDString;
  current_effective_date?: DateYMDString;
  plan_name?: string;
  date_of_birth?: DateYMDString;
  zip_code?: string;
  premium_paid_by?: PremiumPayer;
}

// ==================== POLICY DATA ====================

export interface PolicyData {
  claims?: ClaimsData;
  /**
   * Per-claim-type authority breakdown (Rx carve-out policies only, ADR-077 §7).
   * Present only when per-type authority is resolved; absent => bundled policy.
   * Additive — the pre-resolved top-level totals below already carry the blended
   * values; this drives the per-type source display (chip).
   */
  authority_by_type?: AuthorityByType;
  stoploss?: StoplossData;
  reimbursements?: ReimbursementsData;
  status?: StatusData;
  enrollment?: EnrollmentData;
  total_tasks?: number;
  open_tasks?: number;

  // Policy-period-scoped claimant Note shown on the Claims Summary (US 5273 /
  // ADR-052 §10). The scalar "Note" concept, distinct from threaded comments.
  note?: string;

  // Lasering - per policy year
  is_lasered?: boolean;
  lasered_attributes?: LaseredAttributes;

  // Contract laser / full exclusion (Feature #5287). is_contract_lasered and
  // is_contract_excluded are mutually exclusive.
  is_contract_lasered?: boolean;
  is_contract_excluded?: boolean;
  amt_contract_lasered_deductible?: number;
  contract_incurred_start_date?: DateYMDString;
  contract_incurred_end_date?: DateYMDString;
  contract_paid_start_date?: DateYMDString;
  contract_paid_end_date?: DateYMDString;
  excluded_procedures?: string[];

  /**
   * The specific deductible actually applied for this claimant on this policy
   * after laser precedence (Feature #5287, resolved in the BE's policy_data.sql;
   * consumed by the ADR-077 §6/§7 per-type overage): full exclusion (0) >
   * contract laser > straight laser > base specific deductible. Use this as the
   * denominator when gating "has met deductible" and displaying the deductible
   * amount/percentage — never the base specific_deductible alone. Absent on
   * older/stale snapshots (#5196); consumers fall back to the lasered/base
   * deductible via getEffectiveDeductible().
   */
  effective_specific_deductible?: number;
  /** Where the server took the effective deductible from (Feature #3502). Absent on older snapshots. */
  effective_deductible_source?: EffectiveDeductibleSource;
  effective_deductible_date?: string;
  /**
   * A deductible review is open (#7340): the effective deductible above is the
   * last resolved one, held in force, and stop-loss claims for this claimant and
   * policy are on hold until a user resolves it.
   */
  is_deductible_review_pending?: boolean;

  /**
   * Pre-resolved top-level claim totals (story 3799). Populated server-side from
   * the authority-selected ledger (bundled) or the blended per-type sum
   * (carve-out, ADR-077 §5), so consumers do not re-derive the authority rule.
   * Always present (serialize as 0 when no claim data). For a carve-out claimant
   * these are the blended authoritative values; the per-type split is in
   * `authority_by_type`.
   */
  total_paid?: number;
  total_medical?: number;
  total_rx?: number;
  total_denied?: number;
  total_adjustments?: number;
  spend_over_deductible?: number;
}

// ==================== FAMILY DEDUCTIBLE CONTEXT ====================

/** Enriched family-level deductible data from the subscriber record.
 *  Present only when the claimant has subscriber_ref_id and the policy
 *  has a family-specific deductible configured. */
export interface FamilyDeductibleContext {
  family_total_spend: number;
  family_specific_deductible: number;
  is_family_breach: boolean;
}

// ==================== CLAIMANT ====================

interface ClaimantBase extends ClientRef, PartnerRef {
  claimant_name?: string;
  claimant_first_name: string;
  claimant_last_name: string;
  claimant_gender?: ClaimantGender;
  claimant_birth_date?: DateYMDString;
  claimant_age?: number;
  claimant_relationship?: ClaimantRelationship;

  // Policy-scoped data, keyed by policy_id hex string
  policy_data?: Record<string, PolicyData>;

  // Related subscriber
  claimant_related_subscriber_id?: string;
  claimant_related_subscriber_name?: string;
  claimant_related_subscriber_first_name?: string;
  claimant_related_subscriber_last_name?: string;
  is_claimant_family_deductible?: boolean;

  // Family deductible linking — authoritative FK to subscribers table
  subscriber_ref_id?: string;
  is_pending_link?: boolean;
  family_deductible_context?: FamilyDeductibleContext;

  member_id?: string;
  /**
   * Member id issued by the pharmacy benefit manager on an Rx carve-out policy
   * (#6499/#6514). A DIFFERENT identifier space from `member_id`, which holds the
   * medical TPA's id.
   *
   * On a carve-out policy a claimant legitimately carries BOTH and they will not
   * match — that is the correct state, not a data error. Absent on non-carve-out
   * claimants, which is why the overview grid gates it behind `showWhen`.
   *
   * Editing it changes whether the next Rx document resolves to this claimant or
   * creates a duplicate. The field's helperText says so.
   */
  rx_member_id?: string;
  has_incremental_lasered_deductible?: boolean;

  // Read-only, populated by backend
  policies?: Policy[];
}

export interface Claimant extends ClaimantBase, CommonFields, AuditFields {
  source_documents?: Identifier[];
  /** Live count of open tasks — populated by the list handler, not persisted */
  open_tasks?: number;
  /** Live count of stoploss claims — populated by the list/get handler, not persisted */
  stoploss_claim_count?: number;
}

export type ClaimantCreate = Omit<ClaimantBase, "policy_data" | "policies">;

// ==================== FAMILY UNIT ====================

/**
 * Family member as returned by GET /claimants/subscribers/{id}/family: a
 * `ClaimantRead`, snake_case like every other claimant payload. (The PascalCase
 * shape this replaced predates coronis-apps db5e3c4e2, 2026-05-11; since then
 * every field read through it was undefined, #7393.)
 */
export type FamilyMemberRaw = Claimant;

/**
 * Subscriber record from the family aggregate response: the Go
 * `repository.Subscriber`, whose json tags are snake_case (#7393).
 */
export interface SubscriberRecordRaw {
  id: string;
  aegle_id: string;
  subscriber_id: string; // carrier-assigned ID
  subscriber_name?: string;
  is_resolved: boolean;
  family_total_spend: string; // decimal serialized as string
  /** The policy the family unit's spend is computed on (`RefreshFamilySpend`). */
  policy_id_ref?: string;
}

/** Response shape from GET /claimants/subscribers/{id}/family. */
export interface FamilyAggregateResponse {
  subscriber: SubscriberRecordRaw;
  family_members: FamilyMemberRaw[];
  family_total_spend: string;
  family_specific_deductible?: string;
  individual_limit?: string;
  max_members_counted?: number;
  is_breach: boolean;
  calculation_held: boolean;
}

/** Normalized family member for display. */
export interface FamilyMember {
  id: string;
  aegleId: string;
  name: string;
  relationship: string;
  isPendingLink: boolean;
  memberId?: string;
  totalSpend: number;
  /** From the family payload's live `stoploss_claim_count` (locks Remove / Dissolve). */
  hasStoplossClaims?: boolean;
}
